import { cloneDeep } from 'lodash';
import { MegarubigramStore } from './MegarubigramStore';
import { MegarubigramData, STATS_DIALOG_DELAY } from '../../../utils/types';
import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import { PuzzleNames } from '../../../utils/types';

export const onSolve = () => {
  const { time, puzzleId } = MegarubigramStore.getRawState();

  setTimeout(() => {
    MegarubigramStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);

  savePuzzleProgress(PuzzleNames.Megarubigram, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
    // If they saved progress on this puzzle, it is kinda large compared to other puzzle states.
    // So once they solve it, we overwrite that with empty object to not waste space in mongo.
    partialState: null,
  });
};

export const onReveal = () => {
  MegarubigramStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.Megarubigram, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
      partialState: null, // see above. overwriting any partial saves to save db space.
    });
  });
};

export const saveMegarubigram = () => {
  const { solved, revealed, time, degreesRotated, grids, puzzleId }
    = MegarubigramStore.getRawState();

  if (solved || revealed) return;

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.Megarubigram, {
    puzzleId,
    revealed,
    solved,
    time,
    partialState: {
      degreesRotated,
      grids,
    },
  });
};

export const initMegarubigram = (puzzleData: MegarubigramData) => {
  MegarubigramStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    const scrambledGrid = puzzleData.scrambledGrid;
    const solvedGrid = puzzleData.grid;

    // The server gives the grid data as string[], with each row as a string.
    // This breaks it up into string[][] which is what the frontend code expects.
    const brokenUpScrambledGrid = [];
    const brokenUpSolvedGrid = [];
    for (let n=0; n < scrambledGrid.length; n++) {
      brokenUpScrambledGrid.push(scrambledGrid[n].split(''));
      brokenUpSolvedGrid.push(solvedGrid[n].split(''));
    }

    // we have to take the grids given from the server and break them up into 6x6 matrices first
    const topLeft_solved = extractQuadrant(brokenUpSolvedGrid, 0, 0);
    const topRight_solved = extractQuadrant(brokenUpSolvedGrid, 0, 6);
    const bottomLeft_solved = extractQuadrant(brokenUpSolvedGrid, 6, 0);
    const bottomRight_solved = extractQuadrant(brokenUpSolvedGrid, 6, 6);

    const topLeft = extractQuadrant(brokenUpScrambledGrid, 0, 0);
    const topRight = extractQuadrant(brokenUpScrambledGrid, 0, 6);
    const bottomLeft = extractQuadrant(brokenUpScrambledGrid, 6, 0);
    const bottomRight = extractQuadrant(brokenUpScrambledGrid, 6, 6);

    // Arranged in clockwise order for the sake of the math that
    // determines which grid is in which corner when a quadrant is flipped.
    // (getGridIdAccountingForPuzzleRotation)
    // This makes it so gridId=2 means bottom right and 3 is bottom left.
    const quadrants = [topLeft, topRight, bottomRight, bottomLeft,];
    const solvedQuadrants = [
      topLeft_solved, topRight_solved, bottomRight_solved, bottomLeft_solved,
    ];

    // then we take those 6x6 quadrants and break them up into 3x3 subgrids
    // and assign those to the megarubigram store's data
    s.grids.forEach((grid, gridIndex) => {
      const quadrant = quadrants[gridIndex];
      const solvedQuadrant = solvedQuadrants[gridIndex];
    
      grid.subgrids.forEach((subgrid, subgridIndex) => {
        const row = [0,1].includes(subgridIndex) ? 0 : 3;
        let col = [0,3].includes(subgridIndex) ? 0 : 3;

        subgrid.content = extractSubquadrant(quadrant, row, col);
        subgrid.displayContent = cloneDeep(subgrid.content);
        subgrid.solution = extractSubquadrant(solvedQuadrant, row, col);
      });
    });
  });
};

// Function to extract a 6x6 grid quadrant from a 12x12
const extractQuadrant = (matrix: string[][], rowStart: number, colStart: number) => {
  return matrix.slice(rowStart, rowStart + 6).map(row => row.slice(colStart, colStart + 6));
};

// Function to extract a 3x3 grid quadrant from a 6x6
const extractSubquadrant = (matrix: string[][], rowStart: number, colStart: number) => {
  return matrix.slice(rowStart, rowStart + 3).map(row => row.slice(colStart, colStart + 3));
};

const checkIsSolved = (grids: GridParam[]) => {
  let solved = true;

  for (let n = 0; n < grids.length; n++) {
    for (let i = 0; i < grids[n].subgrids.length; i++) {
      const subgrid = grids[n].subgrids[i];

      for (let row = 0; row < subgrid.content.length; row++) {
        for (let col = 0; col < subgrid.content[row].length; col++) {
          if (subgrid.content[row][col] !== subgrid.solution[row][col]) {
            solved = false;
            break;
          }
        }
        if (!solved) break;
      }

      if (!solved) break;
    }
    if (!solved) break;
  }

  if (solved) onSolve();

  return solved;
};


////////////////////////////////////////////////////////////////////////


export const getFlippedPositionalId = (
  subgridId: number,
  isHFlipped: boolean,
  isVflipped: boolean,
  degreesPuzzleRotated: number,
) => {
  const numPositionShifts = (degreesPuzzleRotated / 90) % 4;

  switch (numPositionShifts) {
    case 0: {
      if (isHFlipped) subgridId = [1, 0, 3, 2][subgridId];
      if (isVflipped) subgridId = [3, 2, 1, 0][subgridId];
      return subgridId;
    }
    case 1:
    case -3: {
      if (isHFlipped) subgridId = [3, 2, 1, 0][subgridId];
      if (isVflipped) subgridId = [1, 0, 3, 2][subgridId];
      return subgridId;
    }
    case 2:
    case -2: {
      if (isHFlipped) subgridId = [1, 0, 3, 2][subgridId];
      if (isVflipped) subgridId = [3, 2, 1, 0][subgridId];
      return subgridId;
    }
    case 3:
    case -1:
    default: { // default only specified for type-compatibility, it should never be relevant
      if (isHFlipped) subgridId = [3, 2, 1, 0][subgridId];
      if (isVflipped) subgridId = [1, 0, 3, 2][subgridId];
      return subgridId;
    }
  }
};

const getGridIdAccountingForRotation = (gridId: number) => {
  const timesPuzzleRotated = MegarubigramStore.getRawState().degreesRotated / 90;

  const sequenceLength = 4;

  // if you do out the examples you'll find this is correct, for both +/- rotations.
  return ((gridId + timesPuzzleRotated) % sequenceLength + sequenceLength) % sequenceLength;
};

export const rotatePuzzleVisual = (clockwise: boolean) => {
  MegarubigramStore.update(s => {
    s.degreesRotated = clockwise ? s.degreesRotated + 90 : s.degreesRotated - 90;

    if (clockwise) {
      rotatePuzzleDataClockwise(s.grids);
    } else {
      rotatePuzzleDataCounterClockwise(s.grids);
    }

    s.solved = checkIsSolved(s.grids);
  });
};

// the `isAccountingForRotation` param avoids an infinite loop when the one fn calls the other when the grid is at 90/-90 degrees.
export const flipSubgridVisualHorizontally = (
  gridIdForCss: number,
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  isAccountingForRotation?: boolean,
) => {
  const positionalGridIdForData = getGridIdAccountingForRotation(gridIdForCss);

  const puzzleRotation = MegarubigramStore.getRawState().degreesRotated;
  const gridRotation = MegarubigramStore.getRawState().grids[gridIdForCss].degreesRotated;
  const gridIsRotated90Degrees = (puzzleRotation + gridRotation) % 180 !== 0;

  if (gridIsRotated90Degrees && !isAccountingForRotation) {
    flipSubgridVisualVertically(gridIdForCss, subgridIdForCss, positionalSubgridIdForData, true);
    return;
  }

  MegarubigramStore.update(s => {
    s.activeGrid = gridIdForCss;
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.grids[gridIdForCss].subgrids[subgridIdForCss];
    const subgridForData = s.grids[positionalGridIdForData].subgrids[positionalSubgridIdForData];

    subgridForCss.flippedHorizontally = !subgridForCss.flippedHorizontally;
    subgridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends
    setTimeout(() => {
      MegarubigramStore.update(_s => {
        _s.grids[gridIdForCss].subgrids[subgridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);
    
    if (isAccountingForRotation) {
      subgridForData.content = flipSubgridDataVertically(subgridForData.content);
    } else {
      subgridForData.content = flipSubgridDataHorizontally(subgridForData.content);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(s.grids[positionalGridIdForData]);
  });
};

export const flipSubgridVisualVertically = (
  gridIdForCss: number,
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  isAccountingForRotation?: boolean,
) => {
  const positionalGridIdForData = getGridIdAccountingForRotation(gridIdForCss);

  const puzzleRotation = MegarubigramStore.getRawState().degreesRotated;
  const gridRotation = MegarubigramStore.getRawState().grids[gridIdForCss].degreesRotated;
  const gridIsRotated90Degrees = (puzzleRotation + gridRotation) % 180 !== 0;

  if (gridIsRotated90Degrees && !isAccountingForRotation) {
    flipSubgridVisualHorizontally(gridIdForCss, subgridIdForCss, positionalSubgridIdForData, true);
    return;
  }


  MegarubigramStore.update(s => {
    s.activeGrid = gridIdForCss;
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.grids[gridIdForCss].subgrids[subgridIdForCss];
    const subgridForData = s.grids[positionalGridIdForData].subgrids[positionalSubgridIdForData];

    subgridForCss.flippedVertically = !subgridForCss.flippedVertically;
    subgridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends
    setTimeout(() => {
      MegarubigramStore.update(_s => {
        _s.grids[gridIdForCss].subgrids[subgridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);

    if (isAccountingForRotation) {
      subgridForData.content = flipSubgridDataHorizontally(subgridForData.content);
    } else {
      subgridForData.content = flipSubgridDataVertically(subgridForData.content);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(s.grids[positionalGridIdForData]);
  });
};

export const rotateSubgridVisual = (
  gridIdForCss: number,
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  clockwise: boolean,
) => {
  const gridIdForData = getGridIdAccountingForRotation(gridIdForCss);

  MegarubigramStore.update(s => {
    s.activeGrid = gridIdForCss;
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.grids[gridIdForCss].subgrids[subgridIdForCss];
    const subgridForData = s.grids[gridIdForData].subgrids[positionalSubgridIdForData];

    if (subgridForCss.flippedHorizontally !== subgridForCss.flippedVertically) {
      subgridForCss.degreesRotated = subgridForCss.degreesRotated + (clockwise ? -90 : 90);
    } else {
      subgridForCss.degreesRotated = subgridForCss.degreesRotated + (clockwise ? 90 : -90);
    }

    // If the grid is flipped, the data subgrid should be rotated the opposite direction of how
    // the css subgrid gets rotated.
    const gridIsFlippedInCss =
      s.grids[gridIdForCss].flippedHorizontally !== s.grids[gridIdForCss].flippedVertically;
    if (gridIsFlippedInCss) clockwise = !clockwise;

    if (clockwise) {
      subgridForData.content = rotateSubgridDataClockwise(subgridForData.content);
    } else {
      subgridForData.content = rotateSubgridDataCounterClockwise(subgridForData.content);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(s.grids[gridIdForData]);
  });
};

export const flipGridVisualVertically = (gridIdForCss: number) => {
  const puzzleRotation = MegarubigramStore.getRawState().degreesRotated;
  const puzzleIsRotated90Degrees = puzzleRotation % 180 !== 0;

  MegarubigramStore.update(s => {
    const positionalGridIdToUpdate = getGridIdAccountingForRotation(gridIdForCss);

    s.activeGrid = gridIdForCss;
    const gridForCss = s.grids[gridIdForCss];
    const gridForData = s.grids[positionalGridIdToUpdate];
    gridForCss.flippedVertically = !gridForCss.flippedVertically;
    gridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends
    setTimeout(() => {
      MegarubigramStore.update(_s => {
        _s.grids[gridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);

    if (puzzleIsRotated90Degrees) {
      flipGridDataHorizontally(gridForData);
    } else {
      flipGridDataVertically(gridForData);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(gridForData);
  });
};

export const flipGridVisualHorizontally = (gridIdForCss: number) => {
  const puzzleRotation = MegarubigramStore.getRawState().degreesRotated;
  const puzzleIsRotated90Degrees = puzzleRotation % 180 !== 0;

  MegarubigramStore.update(s => {
    const positionalGridIdToUpdate = getGridIdAccountingForRotation(gridIdForCss);

    s.activeGrid = gridIdForCss;
    const gridForCss = s.grids[gridIdForCss];
    const gridForData = s.grids[positionalGridIdToUpdate];

    gridForCss.flippedHorizontally = !gridForCss.flippedHorizontally;
    gridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends - might want to do it for ALL not just one,
    // in case they spin the thing during those 0.7s somehow especially with two hands on a touchscreen
    setTimeout(() => {
      MegarubigramStore.update(_s => {
        _s.grids[gridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);

    if (puzzleIsRotated90Degrees) {
      flipGridDataVertically(gridForData);
    } else {
      flipGridDataHorizontally(gridForData);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(gridForData);
  });
};

export const rotateGridVisual = (gridIdForCss: number, clockwise: boolean) => {
  MegarubigramStore.update(s => {
    const positionalGridIdToUpdate = getGridIdAccountingForRotation(gridIdForCss);

    s.activeGrid = gridIdForCss;
    const gridForCss = s.grids[gridIdForCss];
    const gridForData = s.grids[positionalGridIdToUpdate];

    if (gridForCss.flippedHorizontally !== gridForCss.flippedVertically) {
      gridForCss.degreesRotated = gridForCss.degreesRotated + (clockwise ? -90 : 90);
    } else {
      gridForCss.degreesRotated = gridForCss.degreesRotated + (clockwise ? 90 : -90);
    }

    if (clockwise) {
      rotateGridDataClockwise(gridForData);
    } else {
      rotateGridDataCounterClockwise(gridForData);
    }

    s.solved = checkIsSolved(s.grids);
    printQuadrantGrid(gridForData);
  });
};

// These fns below do the actual updates to the grid and subgrid data and are used
// by the visual fns above.
// The visual fns make sure the right thing is updating VISUALLY to the user,
// whereas these functions below just make the more low-level simple changes to specified
// parts of the underlying data matrices. remember, the html content never actually changes.
// Example: user wants to horizontally flip the lower right subgrid, but due to transformations
// that actually means vertically flipping the upper left subgrid. so the visual fn figures out
// which subgrid it REALLY is, then one of these fns below would just flip the upper left subgrid.

const flipSubgridDataHorizontally = (subgrid: string[][]) => {
  const flippedSubgrid = [];
  for (let row of subgrid) {
    flippedSubgrid.push(row.slice().reverse());
  }
  return flippedSubgrid;
};

const flipSubgridDataVertically = (subgrid: string[][]) => {
  const flippedSubgrid = [];
  for (let i = subgrid.length - 1; i >= 0; i--) {
    flippedSubgrid.push(subgrid[i].slice());
  }
  return flippedSubgrid;
};

const rotateSubgridDataClockwise = (subgrid: string[][]) => {
  const rotatedSubgrid: string[][] = [];
  for (let i = 0; i < subgrid.length; i++) {
    rotatedSubgrid.push([]);
    for (let j = 0; j < subgrid.length; j++) {
      rotatedSubgrid[i].push(subgrid[subgrid.length - j - 1][i]);
    }
  }
  return rotatedSubgrid;
};

const rotateSubgridDataCounterClockwise = (subgrid: string[][]) => {
  const rotatedSubgrid: string[][] = [];
  for (let i = 0; i < subgrid.length; i++) {
    rotatedSubgrid.push([]);
    for (let j = 0; j < subgrid.length; j++) {
      rotatedSubgrid[i].push(subgrid[j][subgrid.length - i - 1]);
    }
  }
  return rotatedSubgrid;
};

type GridParam = { subgrids: { content: string[][]; solution: string[][] }[] };

const flipGridDataHorizontally = (grid: GridParam) => {
  grid.subgrids.forEach(subgrid => {
    subgrid.content = flipSubgridDataHorizontally(subgrid.content);
  });

  const subgridsCopy = cloneDeep(grid.subgrids);

  grid.subgrids[0].content = subgridsCopy[1].content;
  grid.subgrids[1].content = subgridsCopy[0].content;
  grid.subgrids[2].content = subgridsCopy[3].content;
  grid.subgrids[3].content = subgridsCopy[2].content;
};

const flipGridDataVertically = (grid: GridParam) => {
  grid.subgrids.forEach(subgrid => {
    subgrid.content = flipSubgridDataVertically(subgrid.content);
  });

  const subgridsCopy = cloneDeep(grid.subgrids);

  grid.subgrids[0].content = subgridsCopy[3].content;
  grid.subgrids[1].content = subgridsCopy[2].content;
  grid.subgrids[2].content = subgridsCopy[1].content;
  grid.subgrids[3].content = subgridsCopy[0].content;
};

const rotateGridDataClockwise = (grid: GridParam) => {
  // First rotate the content of the subgrids
  const rotatedTopLeft = rotateSubgridDataClockwise(grid.subgrids[0].content);
  const rotatedTopRight = rotateSubgridDataClockwise(grid.subgrids[1].content);
  const rotatedBottomRight = rotateSubgridDataClockwise(grid.subgrids[2].content);
  const rotatedBottomLeft = rotateSubgridDataClockwise(grid.subgrids[3].content);

  // Then rotate the position of the subgrids
  // Bottom left becomes top left. Top left becomes top right.
  // Top right becomes bottom right. Bottom right becomes bottom left.
  grid.subgrids[0].content = rotatedBottomLeft;
  grid.subgrids[1].content = rotatedTopLeft;
  grid.subgrids[2].content = rotatedTopRight;
  grid.subgrids[3].content = rotatedBottomRight;
};

const rotateGridDataCounterClockwise = (grid: GridParam) => {
  // First rotate the content of the subgrids
  const rotatedTopLeft = rotateSubgridDataCounterClockwise(grid.subgrids[0].content);
  const rotatedTopRight = rotateSubgridDataCounterClockwise(grid.subgrids[1].content);
  const rotatedBottomRight = rotateSubgridDataCounterClockwise(grid.subgrids[2].content);
  const rotatedBottomLeft = rotateSubgridDataCounterClockwise(grid.subgrids[3].content);

  // Then rotate the position of the subgrids
  grid.subgrids[0].content = rotatedTopRight;
  grid.subgrids[1].content = rotatedBottomRight;
  grid.subgrids[2].content = rotatedBottomLeft;
  grid.subgrids[3].content = rotatedTopLeft;
};

const rotatePuzzleDataClockwise = (grids: GridParam[]) => {
  printParentGrid(grids);
  grids.forEach(grid => {
    rotateGridDataClockwise(grid);
  });

  const gridsCopy = cloneDeep(grids);

  grids[0].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[3].subgrids[index].content;
  });
  grids[1].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[0].subgrids[index].content;
  });
  grids[2].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[1].subgrids[index].content;
  });
  grids[3].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[2].subgrids[index].content;
  });

  printParentGrid(grids);
};


const rotatePuzzleDataCounterClockwise = (grids: GridParam[]) => {
  printParentGrid(grids);
  grids.forEach(grid => {
    rotateGridDataCounterClockwise(grid);
  });

  const gridsCopy = cloneDeep(grids);

  grids[0].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[1].subgrids[index].content;
  });
  grids[1].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[2].subgrids[index].content;
  });
  grids[2].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[3].subgrids[index].content;
  });
  grids[3].subgrids.forEach((subgrid, index) => {
    subgrid.content = gridsCopy[0].subgrids[index].content;
  });

  printParentGrid(grids);
};



///////////




function printParentGrid(grids: GridParam[]): void {
  // only enable this for debugging purposes.
  return;
  // Create an empty 12x12 grid to store the combined result
  const parentGrid: string[][] = Array.from({ length: 12 }, () => Array(12).fill(' '));

  // Function to merge a subgrid into the parent grid at a specific position
  function mergeSubgrid(subgrid: string[][], rowOffset: number, colOffset: number): void {
    for (let r = 0; r < subgrid.length; r++) {
      for (let c = 0; c < subgrid[r].length; c++) {
        parentGrid[rowOffset + r][colOffset + c] = subgrid[r][c];
      }
    }
  }

  // Process each grid
  grids.forEach((grid, gridIndex) => {
    const subgridPositions = [
      [0, 0], [0, 3], [3, 3], [3, 0] // Top-left, Top-right, Bottom-right, Bottom-left
    ];

    grid.subgrids.forEach((subgridData, subgridIndex) => {
      const { content } = subgridData;

      // Calculate position based on the grid index and subgrid position
      const [rowOffset, colOffset] = subgridPositions[subgridIndex];
      mergeSubgrid(content, rowOffset + (gridIndex < 2 ? 0 : 6), colOffset + ([0,3].includes(gridIndex) ? 0 : 6));
    });
  });

  // Print the parent grid
  console.log('\n-------------------------\n')
  parentGrid.forEach(row => {
    console.log(row.join(''));
  });
}



function printQuadrantGrid(grid: GridParam): void {
  // only enable this for debugging purposes.
  return;
  // Create an empty 6x6 grid to store the combined result
  const parentGrid: string[][] = Array.from({ length: 6 }, () => Array(6).fill(' '));

  // Function to merge a subgrid into the parent grid at a specific position
  function mergeSubgrid(subgrid: string[][], rowOffset: number, colOffset: number): void {
    for (let r = 0; r < subgrid.length; r++) {
      for (let c = 0; c < subgrid[r].length; c++) {
        parentGrid[rowOffset + r][colOffset + c] = subgrid[r][c];
      }
    }
  }

  const subgridPositions = [
    [0, 0], [0, 3], [3, 3], [3, 0] // Top-left, Top-right, Bottom-right, Bottom-left
  ];

  // Process each grid
  grid.subgrids.forEach((subgrid, subgridIndex) => {
    const { content } = subgrid;
    const [rowOffset, colOffset] = subgridPositions[subgridIndex];
    mergeSubgrid(content, rowOffset, colOffset);
  });

  // Print the parent grid
  console.log('\n-------------------------\n')
  parentGrid.forEach(row => {
    console.log(row.join(''));
  });
}